import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage immediately to prevent flash
  const [isDark, setIsDark] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('portfolio-theme');
      if (savedTheme) {
        return savedTheme === 'dark';
      }
      // Default to dark theme if no saved preference
      return true;
    } catch {
      // Fallback to dark theme if localStorage is not available
      return true;
    }
  });

  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    // Mark as initialized to prevent flash
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    // Update HTML class for theme consistency - apply immediately
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    if (isDark) {
      htmlElement.classList.remove('light-theme');
      htmlElement.classList.add('dark-theme');
      bodyElement.style.backgroundColor = '#111827'; // gray-900
      bodyElement.style.color = 'white';
    } else {
      htmlElement.classList.remove('dark-theme');
      htmlElement.classList.add('light-theme');
      bodyElement.style.backgroundColor = '#f9fafb'; // gray-50
      bodyElement.style.color = '#111827'; // gray-900
    }
  }, [isDark]);

  // Apply initial theme immediately to prevent flash
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    if (isDark) {
      htmlElement.classList.add('dark-theme');
      bodyElement.style.backgroundColor = '#111827';
      bodyElement.style.color = 'white';
    } else {
      htmlElement.classList.add('light-theme');
      bodyElement.style.backgroundColor = '#f9fafb';
      bodyElement.style.color = '#111827';
    }
  }, []); // Run once on mount

  const toggleTheme = () => {
    // Prevent theme changes during scroll animations
    const isScrolling = window.scrollY !== 0;
    if (isScrolling) {
      // Small delay to prevent flicker during active scrolling
      setTimeout(() => {
        const newTheme = !isDark;
        setIsDark(newTheme);
        localStorage.setItem('portfolio-theme', newTheme ? 'dark' : 'light');
      }, 50);
    } else {
      const newTheme = !isDark;
      setIsDark(newTheme);
      localStorage.setItem('portfolio-theme', newTheme ? 'dark' : 'light');
    }
  };

  // Theme-aware styles
  const theme = {
    isDark,
    toggleTheme,
    
    // Background gradients
    background: {
      primary: isDark 
        ? 'bg-gradient-to-br from-gray-900 via-black to-gray-800' 
        : 'bg-gradient-to-br from-indigo-50 via-white to-cyan-50',
      section: isDark
        ? 'bg-gradient-to-b from-black via-gray-900 to-black'
        : 'bg-gradient-to-b from-white via-indigo-50/30 to-cyan-50/30',
      card: isDark
        ? 'bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur border border-white/10'
        : 'bg-gradient-to-br from-white/80 to-indigo-50/50 backdrop-blur border border-indigo-200/50 shadow-xl shadow-indigo-500/10',
    },

    // Text colors
    text: {
      primary: isDark ? 'text-white' : 'text-slate-900',
      secondary: isDark ? 'text-gray-300' : 'text-slate-700',
      muted: isDark ? 'text-gray-400' : 'text-slate-500',
      gradient: isDark
        ? 'bg-gradient-to-r from-white to-blue-400 bg-clip-text text-transparent'
        : 'bg-gradient-to-r from-slate-900 via-indigo-900 to-cyan-900 bg-clip-text text-transparent',
    },

    // Accent colors for different sections
    accents: {
      hero: isDark ? 'text-blue-400' : 'text-indigo-600',
      about: isDark ? 'text-green-400' : 'text-emerald-600', 
      skills: isDark ? 'text-purple-400' : 'text-violet-600',
      experience: isDark ? 'text-orange-400' : 'text-amber-600',
      projects: isDark ? 'text-cyan-400' : 'text-cyan-600',
      contact: isDark ? 'text-pink-400' : 'text-rose-600',
    },
  };

  return (
    <ThemeContext.Provider value={{ ...theme, isInitialized }}>
      {children}
    </ThemeContext.Provider>
  );
};